import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Printer, Pencil, CheckCircle2, XCircle, Clock3, RotateCcw, PauseCircle, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DatePicker, fmtDate, fmtDateTime } from "@/components/DatePicker";
import { formatPhone } from "@/lib/phone";
import { StatusPill, JOB_ROWS, INTERNAL_APPROVALS } from "@/lib/changeForm";
import { Attachments } from "@/components/Attachments";
import { RequestThread } from "@/components/RequestThread";

const Row = ({ label, value }) => value ? <div className="flex gap-2 text-sm py-1 border-b border-dashed border-border/60 last:border-0"><span className="text-muted-foreground w-48 shrink-0">{label}</span><span className="font-medium">{Array.isArray(value) ? value.join(", ") : value}</span></div> : null;
const Block = ({ title, children }) => {
  const kids = Array.isArray(children) ? children.filter(Boolean) : [children];
  if (!kids.some((k) => k && (k.props?.value || k.type !== Row))) return null;
  return <section className="break-inside-avoid"><h3 className="font-display font-semibold text-sm uppercase tracking-wide text-primary mb-1.5">{title}</h3><div>{children}</div></section>;
};
const fmt = (iso) => fmtDateTime(iso);

function ApprovalRow({ a, canEdit, onSave }) {
  const [name, setName] = useState(a.name || "");
  const [note, setNote] = useState(a.note || "");
  const slug = a.role.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <tr className="border-t border-border" data-testid={`approval-row-${slug}`}>
      <td className="py-2 pr-3 font-medium text-sm">{a.role}</td>
      <td className="py-2 pr-3">{canEdit ? <Input data-testid={`approval-name-${slug}`} className="h-8" placeholder="Printed name" value={name} onChange={(e) => setName(e.target.value)} /> : <span className="text-sm">{a.name || "—"}</span>}</td>
      <td className="py-2 pr-3">
        {a.decision === "approved" && <span className="text-emerald-600 text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>}
        {a.decision === "declined" && <span className="text-rose-600 text-xs font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Declined</span>}
        {a.decision === "pending" && <span className="text-muted-foreground text-xs flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" /> Pending</span>}
        {a.note && <div className="text-xs text-muted-foreground mt-0.5">{a.note}</div>}
      </td>
      <td className="py-2 pr-3 font-mono-num text-xs text-muted-foreground">{a.date ? fmtDate(a.date) : "—"}</td>
      {canEdit && (
        <td className="py-2">
          <div className="flex gap-1 items-center">
            <Input data-testid={`approval-note-${slug}`} className="h-8 w-28" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" variant="outline" className="h-8 text-emerald-600" data-testid={`approval-approve-${slug}`} disabled={!name} onClick={() => onSave(a.role, name, "approved", note)}><CheckCircle2 className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" className="h-8 text-rose-600" data-testid={`approval-decline-${slug}`} disabled={!name} onClick={() => onSave(a.role, name, "declined", note)}><XCircle className="w-4 h-4" /></Button>
          </div>
        </td>
      )}
    </tr>
  );
}

export default function ChangeRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [note, setNote] = useState("");
  const [runDate, setRunDate] = useState("");
  const isAdmin = user.role === "bureau_admin";

  const load = useCallback(() => api.get(`/change-requests/${id}`).then((x) => setR(x.data)).catch(() => toast.error("Request not found")), [id]);
  useEffect(() => { load(); }, [load]);

  const approval = async (role, name, decision, n) => {
    try { await api.post(`/change-requests/${id}/approval`, { role, name, decision, note: n }); toast.success(`${role}: ${decision}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const decide = async (status) => {
    try { await api.post(`/change-requests/${id}/decision`, { status, note, payroll_run_date: runDate || null }); toast.success("Status updated"); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!r) return <div className="text-muted-foreground">Loading…</div>;
  const f = r.form;
  const closed = ["completed", "declined"].includes(r.status);
  const canEditApprovals = !closed;
  const canEditForm = ["submitted", "returned"].includes(r.status);
  const jc = f.job_changes || {};
  const pf = r.approvals.find((a) => a.role === "Payroll / Finance");

  return (
    <div className="space-y-5 animate-fade-up" data-testid="change-request-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/change-requests")} data-testid="crd-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">{f.employee_name} <StatusPill status={r.status} /></h1>
            <p className="text-sm text-muted-foreground mt-1">{r.company_logo} {r.company_name} · Submitted by {r.submitted_by?.name} · {fmt(r.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEditForm && <Button variant="outline" data-testid="crd-edit" onClick={() => navigate(`/change-requests/${id}/edit`)}><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>}
          <Button variant="outline" data-testid="crd-print" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
          <Button variant="outline" className="text-destructive" data-testid="crd-delete" onClick={async () => { if (!window.confirm(`Delete the change request for ${f.employee_name}? This cannot be undone.`)) return; try { await api.delete(`/change-requests/${id}`); toast.success("Change request deleted"); navigate("/change-requests"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }}><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-5">
        {/* Printable document */}
        <Card className="p-6 space-y-5 print:shadow-none print:border-0" data-testid="crd-document">
          <div className="hidden print:block border-b pb-3 mb-3">
            <div className="text-xs text-muted-foreground uppercase">Company name</div>
            <div className="text-xl font-bold">{r.company_name}</div>
            <div className="text-lg font-bold mt-2">CONFIDENTIAL PAYROLL CHANGE FORM</div>
          </div>
          <Block title="Employee Information">
            <Row label="Employee Legal Name" value={f.employee_name} /><Row label="Employee ID" value={f.employee_id} /><Row label="Department" value={f.department} />
            <Row label="Job Title" value={f.job_title} /><Row label="Work Location" value={f.work_location} /><Row label="Supervisor / Manager" value={f.supervisor} />
            <Row label="Requested Effective Date" value={fmtDate(f.effective_date)} /><Row label="Pay Period Effective" value={f.pay_period_effective} />
            <Row label="Employment Status" value={f.employment_status === "Other" ? f.employment_status_other : f.employment_status} />
            <Row label="Pay Frequency" value={f.pay_frequency === "Other" ? f.pay_frequency_other : f.pay_frequency} />
            <Row label="Date Submitted" value={fmtDate(f.date_submitted)} /><Row label="Requested By" value={f.requested_by} />
          </Block>
          <Block title="Type of Change Requested">
            <Row label="Changes" value={f.change_types} /><Row label="Other" value={f.change_type_other} />
          </Block>
          {Object.values(jc).some((v) => v?.current || v?.new) && (
            <section className="break-inside-avoid">
              <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-primary mb-1.5">Job, Status, and Schedule Changes</h3>
              <table className="w-full text-sm"><thead className="text-xs text-muted-foreground text-left"><tr><th className="py-1">Field</th><th>Current</th><th>New</th></tr></thead>
                <tbody>{JOB_ROWS.filter(([k]) => jc[k]?.current || jc[k]?.new).map(([k, l]) => <tr key={k} className="border-t border-border"><td className="py-1.5 text-muted-foreground">{l}</td><td>{jc[k]?.current || "—"}</td><td className="font-medium">{jc[k]?.new || "—"}</td></tr>)}</tbody></table>
            </section>
          )}
          <Block title="Compensation Change">
            <Row label="New Pay Basis" value={f.new_pay_basis} /><Row label="Overtime Eligible" value={f.overtime_eligible} /><Row label="Current Rate / Salary" value={f.current_rate} />
            <Row label="New Rate / Salary" value={f.new_rate} /><Row label="Increase / Decrease" value={f.change_amount} /><Row label="Current Pay Frequency" value={f.current_pay_frequency} />
            <Row label="New Pay Frequency" value={f.new_pay_frequency} /><Row label="Retroactive Effective Date" value={fmtDate(f.retro_date)} />
          </Block>
          <Block title="One-Time or Recurring Adjustment">
            <Row label="Adjustment" value={f.adjustment_types} /><Row label="Other" value={f.adjustment_other} /><Row label="Amount / Rate" value={f.adj_amount} /><Row label="Frequency" value={f.adj_frequency} />
            <Row label="Start / End Date" value={f.adj_dates} /><Row label="Hours / Units" value={f.adj_hours} /><Row label="Earning / Deduction Code" value={f.adj_code} /><Row label="Cost Center / GL" value={f.adj_gl} />
          </Block>
          <Block title="Leave, Return, or Termination">
            <Row label="Action" value={f.leave_action} /><Row label="Leave Pay Status" value={f.leave_pay_status} /><Row label="Last Day Worked" value={fmtDate(f.last_day_worked)} /><Row label="Effective Date" value={fmtDate(f.leave_effective_date)} />
            <Row label="Return Date" value={fmtDate(f.return_date)} /><Row label="Final Pay Date" value={fmtDate(f.final_pay_date)} /><Row label="Termination Category" value={f.termination_category} /><Row label="PTO Payout / Final Instructions" value={f.pto_payout} />
          </Block>
          <Block title="Supporting Documentation"><Row label="Attached" value={f.documents} /><Row label="Other" value={f.documents_other} /></Block>
          <Block title="Reason / Justification"><Row label="Explanation" value={f.justification} /></Block>

          <section className="break-inside-avoid">
            <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-primary mb-1.5">Approvals</h3>
            <table className="w-full"><thead className="text-xs text-muted-foreground text-left"><tr><th className="py-1 pr-3">Approval role</th><th className="pr-3">Printed name</th><th className="pr-3">Decision</th><th className="pr-3">Date</th>{canEditApprovals && !isAdmin && <th className="print:hidden">Record</th>}</tr></thead>
              <tbody>
                {INTERNAL_APPROVALS.map((role) => {
                  const a = r.approvals.find((x) => x.role === role) || { role, decision: "pending" };
                  return <ApprovalRow key={role} a={a} canEdit={canEditApprovals && !isAdmin} onSave={approval} />;
                })}
                <ApprovalRow a={pf || { role: "Payroll / Finance", decision: "pending" }} canEdit={false} />
              </tbody></table>
          </section>

          <section className="break-inside-avoid rounded-lg bg-muted/40 p-4">
            <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-1.5">Payroll Processing — Payroll Use Only</h3>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <Row label="Date Received" value={fmt(r.processing?.received_at)} /><Row label="Received By" value={r.processing?.received_by} />
              <Row label="Payroll Run / Check Date" value={fmtDate(r.processing?.payroll_run_date)} /><Row label="Processed By / Date" value={r.processing?.processed_by ? `${r.processing.processed_by} · ${fmt(r.processing.processed_at)}` : ""} />
              <Row label="Processing Status" value={{ completed: "Completed", returned: "Returned", on_hold: "On hold", declined: "Declined", in_review: "In review", submitted: "Received" }[r.status]} />
              <Row label="Note" value={r.processing?.note} />
            </div>
          </section>
        </Card>

        {/* Side panel */}
        <div className="space-y-4 print:hidden">
          {r.payroll_manager && (
            <Card className="p-4" data-testid="crd-payroll-manager">
              <div className="text-xs text-muted-foreground mb-2">Assigned Payroll Manager</div>
              <div className="flex items-center gap-3">
                <Avatar className="w-11 h-11"><AvatarImage src={r.payroll_manager.avatar} /><AvatarFallback>{r.payroll_manager.name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 text-sm"><div className="font-semibold">{r.payroll_manager.name}</div>
                  <a href={`mailto:${r.payroll_manager.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary truncate"><Mail className="w-3 h-3" />{r.payroll_manager.email}</a>
                  {r.payroll_manager.phone && <a href={`tel:${r.payroll_manager.phone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"><Phone className="w-3 h-3" />{formatPhone(r.payroll_manager.phone)}</a>}
                </div>
              </div>
            </Card>
          )}

          {isAdmin && !closed && (
            <Card className="p-4 space-y-3" data-testid="crd-decision-panel">
              <div className="font-display font-semibold">Payroll Manager Decision</div>
              <DatePicker testid="crd-run-date" value={runDate} onChange={setRunDate} placeholder="Payroll run / check date" />
              <Textarea data-testid="crd-note" rows={3} placeholder="Note to submitter / processing note" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                {r.status !== "in_review" && <Button variant="outline" data-testid="crd-in-review" onClick={() => decide("in_review")}><Clock3 className="w-4 h-4 mr-1" /> In Review</Button>}
                <Button variant="outline" data-testid="crd-on-hold" onClick={() => decide("on_hold")}><PauseCircle className="w-4 h-4 mr-1" /> On Hold</Button>
                <Button variant="outline" data-testid="crd-return" onClick={() => decide("returned")}><RotateCcw className="w-4 h-4 mr-1" /> Return</Button>
                <Button variant="outline" className="text-rose-600" data-testid="crd-decline" onClick={() => decide("declined")}><XCircle className="w-4 h-4 mr-1" /> Decline</Button>
                <Button className="col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="crd-complete" onClick={() => decide("completed")}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve & Mark Completed</Button>
              </div>
            </Card>
          )}

          <Attachments entityType="change_request" entityId={id} readOnly={closed} />

          <Card className="p-4" data-testid="crd-thread"><RequestThread requestId={id} messages={r.messages || []} side="hr" closed={closed} onSent={load} /></Card>

          <Card className="p-4" data-testid="crd-timeline">
            <div className="font-display font-semibold mb-3">Activity</div>
            <ol className="space-y-3">
              {[...r.timeline].reverse().map((t, i) => (
                <li key={i} className="flex gap-3 text-sm"><span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" /><div><div className="font-medium">{t.event}</div><div className="text-xs text-muted-foreground">{t.by} · {fmt(t.at)}</div></div></li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}
